import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axiosInstance from '../../../../axios/axiosInstance';
import { ExerciseFormat } from 'model/response';
import React, { useEffect, useState } from 'react';
import { Checkbox, Input, message, Popconfirm, Select, Table } from 'antd';
import { useMasterGradesSelector, useMasterSubjectsSelector } from '../../../../redux/selector';
import { ColumnsType } from 'antd/es/table/interface';
import ExerciseFormatFormModal from './ExerciseFormatFormModal';
import { ModePage } from 'common/enum';
import processError from '../../../../utils/processError';
import {
  checkExistQuestion,
  findAllExerciseFormats,
  toggleExerciseFormatChild,
  toggleExerciseFormatWebviewActive,
} from 'service/exerciseFormatService';
import { mapToSelectItems } from '../../../../utils/utils';
import { setExerciseFormats } from '../../../../redux/slice/masterDataSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../redux/store/store';
import WebViewActiveCheckbox from '../../../common/WebViewActiveCheckbox';
import useTableConfig from '../../../../hooks/useTableConfig';

export default function ExerciseFormatManagement() {
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig();
  const grades = useMasterGradesSelector();
  const subjects = useMasterSubjectsSelector();
  const [subjectId, setSubjectId] = useState('');
  const [keyword, setKeyword] = useState<string>('');
  const [gradeId, setGradeId] = useState<string>('');
  const [exerciseFormatId, setExerciseFormatId] = useState<string>('');
  const [mode, setMode] = useState<ModePage>(ModePage.ADD);
  const [isOpen, setOpenModal] = useState<boolean>(false);

  const [data, setData] = useState<ExerciseFormat[]>();
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const columns: ColumnsType<ExerciseFormat> = [
    {
      title: 'Dạng bài tập',
      dataIndex: 'name',
      width: '40%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('name'),
    },
    {
      title: 'Mã',
      dataIndex: 'code',
      sorter: true,
      sortOrder: getColumnSorter('code'),
    },
    {
      title: 'Lớp',
      dataIndex: 'grade.name',
      render: (_, record) => (record.grade ? grades.find((grade) => grade.id === record.grade.id)?.name : ''),
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('grade.name'),
    },
    {
      title: 'Môn học',
      dataIndex: 'subject.name',
      render: (_, record) => (record.subject ? subjects.find((subject) => subject.id === record.subject.id)?.name : ''),
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('subject.name'),
    },
    {
      title: 'Hiển thị',
      dataIndex: 'webViewActive',
      align: 'center',
      render: (_, record) => (
        <WebViewActiveCheckbox
          currentActiveValue={record.webViewActive}
          itemName={'dạng bài tập'}
          setLoading={setLoading}
          toggleWebviewActive={async () => toggleExerciseFormatWebviewActive(record.id)}
          reloadPageData={loadListExerciseFormats}
        />
      ),
      sorter: true,
      sortOrder: getColumnSorter('webViewActive'),
    },
    {
      title: 'Ẩn trong mức độ thông thạo',
      width: '15%',
      dataIndex: 'child',
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title={record.child ? `Hiển thị trong mức độ thông thạo?` : `Ẩn trong mức độ thông thạo?`}
          description={'Xác nhận'}
          onConfirm={() => handleToggleChild(record.id)}
          okText="Đồng ý"
          cancelText="Hủy"
        >
          <Checkbox checked={record.child} />
        </Popconfirm>
      ),
      sorter: true,
      sortOrder: getColumnSorter('child'),
    },
    {
      title: 'Thứ tự',
      dataIndex: 'webViewOrder',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('webViewOrder'),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      align: 'center',
      render: (_, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={(e) => moveEditView(e, record)}>
            <EditOutlined className="" />
          </button>
          <Popconfirm
            title="Xóa dạng bài tập"
            description="Xác nhận xóa dạng bài tập. Hành động này không thể hoàn tác?"
            onConfirm={() => deleteExerciseFormat(record)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const deleteExerciseFormat = async (record: ExerciseFormat) => {
    setLoading(true);

    try {
      const resIsExistQuestion = await checkExistQuestion(record.id);
      if (resIsExistQuestion.data === true) {
        message.error('Đã tồn tại câu hỏi liên quan đến dạng bài tập');
        setLoading(false);
        return;
      }

      await axiosInstance({
        url: `/exercise-formats/${record.id}`,
        method: 'delete',
      });
      message.success('Xoá thành công.');
      await loadListExerciseFormats();
    } catch (err) {
      processError(err);
    }
    setLoading(false);
  };

  const moveEditView = async (e: any, record: ExerciseFormat) => {
    setMode(ModePage.EDIT);
    setOpenModal(true);
    setExerciseFormatId(record.id);
  };

  const loadListExerciseFormats = async function () {
    setLoading(true);
    const res = await axiosInstance({
      url: '/exercise-formats/page',
      params: {
        keyword,
        size: pageSize,
        page: currentPage,
        sortBy,
        direction,
        subjectId,
        gradeId: gradeId,
        fromCMS: true,
      },
    });

    if (res.status === 200) {
      setData(res.data.content);
      setLoading(false);
      setTotalElements(res.data.totalElements);
    }
  };

  const onChangeSelect = function (e: any, fieldName: string) {
    switch (fieldName) {
      case 'schoolYear':
        setGradeId(e);
        break;
      default:
        break;
    }
  };

  const onSearch = function (e: any) {
    setKeyword(e.target.value);
  };

  const addNewExerciseFormat = async () => {
    setMode(ModePage.ADD);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleBeforeSuccess = () => {
    setOpenModal(false);
    loadListExerciseFormats().then();
    findAllExerciseFormats()
      .then((exerciseFormats) => {
        dispatch(setExerciseFormats(exerciseFormats));
      })
      .catch((error) => console.error(error));
  };

  useEffect(() => {
    loadListExerciseFormats().then();
  }, [currentPage, pageSize, sortBy, direction, gradeId, subjectId, keyword]);

  const handleToggleChild = async (id: string) => {
    try {
      setLoading(true);
      await toggleExerciseFormatChild(id);
      await loadListExerciseFormats();
    } catch (err) {
      processError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-[calc(100vh-112px)] overflow-y-auto p-4">
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={() => addNewExerciseFormat()}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
          <div className="min-w-[170px] max-w-[170px] flex items-center gap-2">
            <div>Năm</div>
            <Select
              onChange={(e) => onChangeSelect(e, 'schoolYear')}
              className="w-full"
              placeholder="Bộ lọc-Năm học"
              style={{ flex: 1 }}
              value={gradeId}
              options={[
                {
                  value: '',
                  label: 'Tất cả',
                },
              ].concat(
                grades.map((grade) => ({
                  value: grade.id,
                  label: grade.name,
                })),
              )}
            />
          </div>
          <div className="flex items-center gap-2">
            <div>Môn học</div>
            <Select
              onChange={(e) => setSubjectId(e)}
              className="w-full min-w-[240px]"
              placeholder="Bộ lọc - Môn học"
              style={{ flex: 1 }}
              value={subjectId}
              options={[
                {
                  value: '',
                  label: 'Tất cả',
                },
              ].concat(mapToSelectItems(subjects))}
            />
          </div>
        </div>
        <Input placeholder="Nhập từ khóa tìm kiếm" name="keyword" className="w-fit" onPressEnter={(e) => onSearch(e)} />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      <ExerciseFormatFormModal
        isOpen={isOpen}
        onCloseModal={handleCloseModal}
        mode={mode}
        onBeforeSuccess={handleBeforeSuccess}
        exerciseFormatId={exerciseFormatId}
      />
    </div>
  );
}
